import {
  CartItem,
  Item,
  ItemOption,
  MenuCategory,
  MenuItem,
} from "../types/item";

export const optionAmount = (sub: {
  setsCharge?: boolean;
  price?: number;
  extraPrice?: number;
}) =>
  sub.setsCharge
    ? Number(sub.price ?? sub.extraPrice) || 0
    : Number(sub.extraPrice ?? sub.price) || 0;

export const selectedChoiceIds = (item: Item) => {
  if (item.options?.length) {
    return item.options.flatMap((option) =>
      option.subOptions
        .filter((sub) => sub.checked && sub.isAvailable && sub.uuid)
        .map((sub) => sub.uuid as string),
    );
  }
  return (item.choices ?? []).map((choice) => choice.uuid).filter(Boolean);
};

export const cloneOptions = (options?: ItemOption[]): ItemOption[] =>
  (options ?? []).map((option) => ({
    ...option,
    subOptions: option.subOptions.map((sub) => ({ ...sub })),
  }));

export const cloneItem = <T extends Item>(item: T): T => ({
  ...item,
  options: cloneOptions(item.options),
});

export const lineIdentity = (item: Item) =>
  JSON.stringify({
    uuid: item.uuid,
    choices: [...selectedChoiceIds(item)].sort(),
    notes: item.notes || "",
  });

export const checkedCount = (option: ItemOption) =>
  option.subOptions.filter((sub) => sub.checked && sub.isAvailable).length;

export const selectionBounds = (
  option: Pick<ItemOption, "type" | "required" | "min" | "max">,
) => {
  const pickOne = option.type !== "checkbox";
  const min = option.min != null ? option.min : option.required ? 1 : 0;
  const max = pickOne ? (option.max ?? 1) : option.max;
  return { min, max };
};

export const selectionFilled = (option: ItemOption, count: number) => {
  const { min, max } = selectionBounds(option);
  const available = option.subOptions.filter((sub) => sub.isAvailable).length;
  if (!available) return false;
  const goal = max ?? (min > 0 ? min : available);
  return count >= goal;
};

export const selectionOutOfRange = (option: ItemOption) => {
  const count = checkedCount(option);
  const { min, max } = selectionBounds(option);
  return count < min || (max != null && count > max);
};

export const selectionHint = (option: ItemOption) => {
  const { min, max } = selectionBounds(option);
  if (max === 1 && min <= 1) return "You can select 1";
  if (max != null && min === max) return `You can select ${min}`;
  if (max != null && min > 0) return `You can select ${min} to ${max}`;
  if (min > 0) return `You can select at least ${min}`;
  if (max != null) return `You can select up to ${max}`;
  return "You can select any";
};

export const selectionRibbon = (option: ItemOption, count: number) => {
  const { min, max } = selectionBounds(option);
  const met = count >= min && (max == null || count <= max);
  if (min > 0 && !met) return { type: "critical" as const, text: "Required" };
  if (min > 0) return { type: "success" as const, text: "Completed" };
  return { type: "success" as const, text: "Optional" };
};

export const missingRequiredGroup = (item: Item) =>
  (item.options ?? []).find((option) => selectionOutOfRange(option));

export const toQuoteItem = (item: CartItem) => {
  const line: {
    uuid: string;
    quantity: number;
    choices: string[];
    notes?: string;
  } = {
    uuid: item.uuid,
    quantity: item.quantity,
    choices: selectedChoiceIds(item),
  };
  if (item.notes) line.notes = item.notes;
  return line;
};

const mapMenuItem = (item: MenuItem): Item => {
  const groups = item.optionGroups ?? [];
  const chargePrices = groups
    .filter((group) => group.setsPrice)
    .flatMap((group) =>
      group.choices
        .filter((choice) => choice.isAvailable)
        .map((choice) => Number(choice.price) || 0),
    );
  const hasCharge = chargePrices.length > 0;
  const basePrice = hasCharge
    ? Math.min(...chargePrices)
    : Number(item.price) || 0;
  const options: ItemOption[] = groups.map((group) => {
    const pickOne = group.pick !== "many";
    return {
      uuid: group.uuid,
      name: group.name,
      type: pickOne ? "radio" : "checkbox",
      required: group.required,
      min: group.min,
      max: group.max == null ? undefined : group.max,
      subOptions: group.choices.map((choice) => ({
        uuid: choice.uuid,
        name: choice.name,
        price: choice.price,
        extraPrice: group.setsPrice ? 0 : choice.price,
        isAvailable: choice.isAvailable,
        setsCharge: group.setsPrice,
      })),
    };
  });
  return {
    uuid: item.uuid,
    name: item.name,
    desc: item.description || "",
    price: hasCharge ? 0 : basePrice,
    previousPrice: item.previousPrice ?? undefined,
    imageUrl: item.imageUrl || "",
    isAvailable: item.isAvailable,
    hasVariations: hasCharge,
    subcategoryUuid: item.subcategoryUuid || undefined,
    favorite: false,
    totalPrice: basePrice,
    options,
    isPromotional: item.isPromotional,
    promotionalImageUrl: item.promotionalImageUrl,
  };
};

export const normalizeCategory = (
  category: Omit<MenuCategory, "items"> & { items?: MenuItem[] },
): MenuCategory => ({
  uuid: category.uuid,
  name: category.name,
  imageUrl: category.imageUrl,
  subcategories: category.subcategories ?? [],
  items: (category.items ?? []).map(mapMenuItem),
});

export const normalizeMenu = (data: {
  categories: (Omit<MenuCategory, "items"> & { items?: MenuItem[] })[];
  popularItemIds: string[];
  cartSuggestions?: string[];
}): { categories: MenuCategory[]; cartSuggestions: Item[] } => {
  const categories: MenuCategory[] = (data?.categories ?? []).map(
    normalizeCategory,
  );
  const byId = new Map(
    categories.flatMap((category) =>
      category.items.map((item) => [item.uuid, item] as const),
    ),
  );
  const pick = (ids: string[] = []) => {
    const seen = new Set<string>();
    return ids.flatMap((id) => {
      if (!id || seen.has(id)) return [];
      const item = byId.get(id);
      if (!item) return [];
      seen.add(id);
      return [item];
    });
  };
  const popular = pick(data?.popularItemIds);
  return {
    categories: [
      {
        uuid: "popular",
        name: "Popular Items",
        isPopular: true,
        subcategories: [
          {
            isPopular: true,
            uuid: "popular-items",
            name: "Popular Items",
          },
        ],
        items: popular.map((item) => ({
          ...item,
          subcategoryUuid: "popular-items",
        })),
      },
      ...categories,
    ],
    cartSuggestions: pick(data?.cartSuggestions),
  };
};

export const previewLineTotal = (item: Item, quantity = 1) => {
  const checked = (item.options ?? []).flatMap((option) =>
    option.subOptions.filter((sub) => sub.checked),
  );
  const charges = checked.filter((sub) => sub.setsCharge);
  const base = charges.length
    ? charges.reduce((sum, sub) => sum + optionAmount(sub), 0)
    : Number(item.price) || 0;
  const addons = checked
    .filter((sub) => !charges.includes(sub))
    .reduce((sum, sub) => sum + optionAmount(sub), 0);
  return (base + addons) * quantity;
};

export const itemsBySubcategory = (category: MenuCategory) => {
  const grouped = (category.subcategories ?? [])
    .map((subcategory) => ({
      subcategory,
      items: category.items.filter(
        (item) => item.subcategoryUuid === subcategory.uuid,
      ),
    }))
    .filter((group) => group.items.length);
  const groupedIds = new Set(
    grouped.flatMap((group) => group.items.map((item) => item.uuid)),
  );
  const ungrouped = category.items.filter(
    (item) => !groupedIds.has(item.uuid),
  );
  return { ungrouped, grouped };
};
